// ── fuzzyScore ────────────────────────────────────────────────────────────
// Case-insensitive relevance score, or null when str doesn't match query at
// all. Higher is a better match. Tiers, best to worst:
//   exact match > starts with query > query at a word boundary >
//   query as a substring (earlier position scores higher) >
//   in-order subsequence (e.g. 'brkn bd' matches 'Breaking Bad', penalized by
//   how many gap characters it has to skip).
// Search UIs that want ranked results should sort on this instead of treating
// every match as equally relevant — a plain filter buries an exact title hit
// under weak subsequence matches with no way to tell them apart (Jason
// 2026-07-03: "fuzzy search... doesn't seem like it's the most intelligent
// version").
export function fuzzyScore(str, query) {
  const q = (query || '').toLowerCase().trim()
  if (!q) return 0
  const s = (str || '').toLowerCase()
  if (!s) return null

  if (s === q) return 100
  if (s.startsWith(q)) return 90

  const idx = s.indexOf(q)
  if (idx !== -1) {
    const atWordBoundary = idx === 0 || /[\s:\-–—(),.'"!?]/.test(s[idx - 1])
    const base = atWordBoundary ? 80 : 65
    return base - Math.min(idx, 30) * 0.3
  }

  // Subsequence fallback: every char of q must appear in order in s.
  let si = 0, first = -1, gaps = 0
  for (let qi = 0; qi < q.length; qi++) {
    const found = s.indexOf(q[qi], si)
    if (found === -1) return null
    if (first === -1) first = found
    else gaps += found - si
    si = found + 1
  }
  return Math.max(5, 40 - gaps * 2 - first * 0.2)
}

// ── fuzzyMatch ────────────────────────────────────────────────────────────
// Boolean wrapper over fuzzyScore for callers that only need a yes/no filter.
export function fuzzyMatch(str, query) {
  if (!query) return true
  return fuzzyScore(str, query) !== null
}
